// Template 11 — "Onyx" — a dark luxe editorial landing page with a distinct
// layout: a full-bleed cinematic hero banner (course image + overlaid headline +
// a centered play that opens the preview), a stats rail, Bodoni Moda high-fashion
// serif, film grain and a champagne-gold accent. Fully theme-driven.
import { Fragment, useState } from "react";
import RichText from "../RichText";
import { Icon, Stars, splitTitle, rgba, shade, useGoogleFonts, usePreviewVideo, VideoModal, SOCIAL_ICONS, orderedSections, skillIconD, PolicyLinks, GuaranteeBadge, Reveal, LessonIcon } from "./shared";

const SIGNATURE2 = "#0E7C66";
const SIGNATURE = "#C9A24B";     // champagne gold — luxe on black
const BG = "#0b0a0c";            // warm near-black
const CARD = "#161418";          // dark card
const FOOT = "#070607";          // footer
const LINE = "rgba(255,255,255,.09)";
const TXT = "#f4f2f0";
const MUT = "rgba(244,242,240,.55)";
const SERIF = "'Bodoni Moda', Georgia, serif";
const SANS = "'Inter', system-ui, sans-serif";

export default function Template11({ course, landing, instructor, modules, themeColor, themeColor2, canView, labels = {}, onEnroll, policies }) {
  useGoogleFonts("https://fonts.googleapis.com/css2?family=Bodoni+Moda:ital,opsz,wght@0,6..96,500;0,6..96,600;0,6..96,700;1,6..96,600&family=Inter:wght@400;500;600;700&display=swap");
  const vid = usePreviewVideo();
  const A = themeColor || SIGNATURE;
  const AD = shade(A, -0.16);
  const AL = shade(A, 0.35);
  const B = themeColor2 || SIGNATURE2;   // secondary branding accent
  const grad = `linear-gradient(135deg, ${A}, ${AD})`;
  const pal = { A, AD, AL, B, grad };

  const header = landing.header || {};
  const t = splitTitle(header.headline || course.name);
  const sub = header.subheadline || course.short_description || "";
  const isPaid = course.access_type === 2;
  const priceLabel = isPaid && course.price > 0 ? course.price : (labels.free || "Free");
  const ctaLabel = isPaid ? (labels.buy_now || "Buy Now") : (labels.enroll_now || "Enroll Now");
  const statusLabel = isPaid ? "Premium course" : course.access_type === 3 ? (canView ? "Unlocked" : "Members only") : "Free course";
  const lessonCount = course.total_lessons || modules.reduce((a, m) => a + (m.lessons?.length || 0), 0);
  const hasVideo = landing.video?.show !== false && !!landing.video?.url;
  const poster = landing.video?.thumb || course.course_image;

  const order = orderedSections(landing.section_order);

  const navLinks = [
    landing.about?.show !== false && (landing.description || course.description) && { href: "#t11-about", label: "About" },
    landing.skill?.show && landing.skill.list?.length > 0 && { href: "#t11-skill", label: "Skills" },
    modules.length > 0 && landing.course_include?.show !== false && { href: "#t11-curriculum", label: labels.curriculum || "Curriculum" },
    instructor?.name && landing.instructor?.show !== false && { href: "#t11-instructor", label: "Instructor" },
    landing.testimonials?.show && landing.testimonials.list?.length > 0 && { href: "#t11-reviews", label: labels.reviews || "Reviews" },
    landing.faq?.show && landing.faq.list?.length > 0 && { href: "#t11-faq", label: labels.faq || "FAQ" },
  ].filter(Boolean);

  const stats = [
    course.star_rating > 0 && { v: Number(course.star_rating).toFixed(1), k: labels.rating || "Rating" },
    course.total_lessons > 0 && { v: course.total_lessons, k: labels.lessons || "Lessons" },
    modules.length > 0 && { v: modules.length, k: labels.modules || "Modules" },
    { v: "∞", k: "Lifetime" },
  ].filter(Boolean);

  const blocks = {
    about: landing.about?.show !== false && (landing.description || course.description) && (
      <Reveal><Sec id="t11-about" kicker="Overview" title="About this course" pal={pal}>
        <div className="rich-content mt-6 max-w-3xl text-[15px] leading-relaxed" style={{ color: MUT }}><RichText html={landing.description || course.description} /></div>
      </Sec></Reveal>
    ),
    instructor: landing.instructor?.show !== false && instructor?.name && (
      <Reveal><div id="t11-instructor"><Sec kicker="Instructor" title={labels.meet_instructor || "Meet the instructor"} pal={pal}>
        <div className="mt-8 grid gap-8 sm:grid-cols-[210px_1fr]">
          <div className="relative mx-auto w-full max-w-[210px]">
            <div className="absolute -bottom-3 -right-3 h-full w-full" style={{ border: `1px solid ${A}` }} />
            <div className="relative aspect-[4/5] w-full overflow-hidden" style={{ background: CARD, border: `1px solid ${LINE}`, boxShadow: `0 26px 52px -26px ${rgba(A, 0.5)}` }}>
              {instructor.profile_image ? <img src={instructor.profile_image} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full w-full items-center justify-center text-7xl italic" style={{ fontFamily: SERIF, color: rgba(A, 0.4) }}>{instructor.name.charAt(0)}</span>}
            </div>
          </div>
          <div className="flex flex-col justify-center text-center sm:text-left">
            <span className="text-[0.7rem] font-bold uppercase tracking-[0.2em]" style={{ color: A }}>Course Instructor</span>
            <h3 className="mt-2 text-[2rem] leading-tight text-white" style={{ fontFamily: SERIF, fontWeight: 600 }}>{instructor.name}</h3>
            {instructor.bio && <p className="mt-4 max-w-lg text-[15px] leading-relaxed" style={{ color: MUT }}>{instructor.bio}</p>}
          </div>
        </div>
      </Sec></div></Reveal>
    ),
    skill: landing.skill?.show && landing.skill.list?.length > 0 && (
      <Reveal><Sec id="t11-skill" kicker={labels.what_youll_learn || "What you'll learn"} title={landing.skill.heading || "Skills you will walk away with"} pal={pal}>
        <div className="mt-8 grid gap-x-10 gap-y-6 sm:grid-cols-2">
          {landing.skill.list.map((s, i) => (
            <div key={i} className="flex items-start gap-4 border-t pt-6" style={{ borderColor: LINE }}>
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-black" style={{ background: A }}><Icon d={skillIconD(landing.skill.icon)} className="h-4 w-4" stroke={2.2} /></span>
              <span className="text-[15px] leading-snug text-white/85">{s}</span>
            </div>
          ))}
        </div>
        {landing.skill.long_desc && <div className="rich-content mt-6 max-w-3xl text-[15px] leading-relaxed" style={{ color: MUT }} dangerouslySetInnerHTML={{ __html: landing.skill.long_desc }} />}
      </Sec></Reveal>
    ),
    include: modules.length > 0 && landing.course_include?.show !== false && (
      <Reveal><div id="t11-curriculum"><Sec kicker={labels.curriculum || "Curriculum"} title={labels.what_inside_course || "What's inside the course"} sub={`${modules.length} ${labels.modules || "Modules"} · ${lessonCount} ${labels.lessons || "Lessons"}`} pal={pal}>
        <Accordion modules={modules} pal={pal} labels={labels} />
      </Sec></div></Reveal>
    ),
    testimonial: <div id="t11-reviews"><Testimonials testimonials={landing.testimonials} pal={pal} labels={labels} /></div>,
    moneyback: isPaid && <Guarantee guarantee={landing.guarantee} pal={pal} />,
    faq: <div id="t11-faq"><Faq faq={landing.faq} pal={pal} labels={labels} /></div>,
  };

  return (
    <div className="relative min-h-screen" style={{ background: BG, color: TXT, fontFamily: SANS }}>
      <Grain />
      {/* TOP BAR — sticky */}
      <div className="sticky top-0 z-50 backdrop-blur-md" style={{ background: rgba(BG, 0.72), borderBottom: `1px solid ${rgba("#ffffff", 0.06)}` }}>
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-5 sm:px-8">
          <a href="#top" className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center text-black" style={{ background: A }}><Icon d="M12 14l9-5-9-5-9 5 9 5z M12 14l6.16-3.42a12 12 0 01.34 3.42c0 3-3 5-6.5 5S5.5 17 5.5 14a12 12 0 01.34-3.42L12 14z" className="h-4 w-4" /></span>
            <span className="max-w-[42vw] truncate text-[1.15rem] text-white sm:max-w-sm" style={{ fontFamily: SERIF, fontWeight: 600 }}>{course.name}</span>
          </a>
          <nav className="hidden items-center gap-6 lg:flex">
            {navLinks.map((l) => <a key={l.href} href={l.href} className="text-[0.7rem] font-bold uppercase tracking-[0.16em] text-white/55 transition hover:text-white">{l.label}</a>)}
          </nav>
          <button onClick={onEnroll} className="rounded-full px-5 py-2 text-[0.72rem] font-bold uppercase tracking-[0.14em] text-black transition hover:-translate-y-0.5" style={{ background: A }}>{ctaLabel}</button>
        </div>
      </div>

      {/* HERO — full-bleed cinematic banner */}
      <header id="top" className="relative -mt-16 flex min-h-[82vh] items-end overflow-hidden">
        {(header.bg_image || poster) ? <img src={header.bg_image || poster} alt="" className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${rgba(A, 0.5)}, ${BG})` }} />}
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${BG} 6%, ${rgba(BG, 0.4)} 45%, ${rgba(BG, 0.65)} 100%)` }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(60% 50% at 20% 90%, ${rgba(A, 0.18)}, transparent 60%)` }} />
        <div className="absolute inset-0" style={{ background: `radial-gradient(48% 44% at 90% 8%, ${rgba(B, 0.16)}, transparent 62%)` }} />

        {/* centered play — opens preview popup */}
        {hasVideo && (
          <button onClick={vid.play} className="group absolute left-1/2 top-[42%] z-10 -translate-x-1/2 -translate-y-1/2" aria-label="Play preview">
            <span className="relative flex items-center justify-center">
              <span className="absolute h-24 w-24 animate-ping rounded-full opacity-60" style={{ background: rgba(A, 0.3) }} />
              <span className="absolute h-28 w-28 rounded-full" style={{ boxShadow: `inset 0 0 0 1px ${rgba(A, 0.5)}` }} />
              <span className="relative flex h-20 w-20 items-center justify-center rounded-full text-black transition-transform duration-300 group-hover:scale-110" style={{ background: A, boxShadow: `0 16px 44px ${rgba(A, 0.55)}` }}>
                <svg viewBox="0 0 24 24" className="ml-1 h-8 w-8 fill-current"><path d="M8 5v14l11-7z" /></svg>
              </span>
            </span>
          </button>
        )}

        <div className="relative mx-auto w-full max-w-6xl px-5 pb-16 sm:px-8 lg:pb-20">
          <div className="flex items-center gap-3">
            <span className="h-px w-10" style={{ background: A }} />
            <span className="text-[0.68rem] font-bold uppercase tracking-[0.24em]" style={{ color: A }}>{statusLabel}</span>
          </div>
          <h1 className="mt-5 max-w-3xl text-[2.9rem] leading-[0.98] text-white sm:text-[4.6rem]" style={{ fontFamily: SERIF, fontWeight: 600 }}>
            {t.before} <em style={{ fontStyle: "italic", color: A }}>{t.accent}</em> {t.after}
          </h1>
          {sub && <p className="mt-5 max-w-xl text-[16px] leading-relaxed text-white/70">{sub}</p>}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button onClick={onEnroll} className="group inline-flex items-center gap-2.5 rounded-full px-7 py-3.5 text-[0.78rem] font-bold uppercase tracking-[0.14em] text-black transition hover:-translate-y-0.5" style={{ background: A, boxShadow: `0 14px 34px ${rgba(A, 0.4)}` }}>{ctaLabel} <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>
            <a href="#t11-curriculum" className="inline-flex items-center gap-2 rounded-full border px-6 py-3.5 text-[0.78rem] font-bold uppercase tracking-[0.14em] text-white transition hover:bg-white/5" style={{ borderColor: rgba("#ffffff", 0.25) }}>{labels.preview || "Preview Course"}</a>
          </div>
        </div>
      </header>

      {/* STATS RAIL */}
      <div className="border-y" style={{ borderColor: LINE, background: CARD }}>
        <div className="mx-auto grid max-w-6xl grid-cols-2 sm:grid-cols-4">
          {stats.map((s, i) => (
            <div key={i} className="px-6 py-6 text-center" style={{ borderLeft: i % 4 === 0 ? "none" : `1px solid ${LINE}`, borderTop: i >= 2 ? `1px solid ${LINE}` : "none" }}>
              <div className="text-3xl sm:text-4xl" style={{ fontFamily: SERIF, fontWeight: 600, color: A }}>{s.v}</div>
              <div className="mt-1 text-[0.68rem] font-semibold uppercase tracking-[0.16em]" style={{ color: MUT }}>{s.k}</div>
            </div>
          ))}
        </div>
      </div>

      {/* BODY */}
      <div className="mx-auto max-w-5xl px-5 py-16 sm:px-8 lg:py-24">
        {/* ENROLL PANEL */}
        <Reveal>
          <div className="mb-20 overflow-hidden border" style={{ background: CARD, borderColor: LINE }}>
            <div className="grid gap-6 p-6 sm:grid-cols-[auto_1fr_auto] sm:items-center sm:gap-9 sm:p-8">
              <div className="text-center sm:border-r sm:pr-9 sm:text-left" style={{ borderColor: LINE }}>
                <span className="text-[0.66rem] font-bold uppercase tracking-[0.2em]" style={{ color: A }}>{statusLabel}</span>
                <div className="mt-1 text-[3rem] leading-none" style={{ fontFamily: SERIF, fontWeight: 600, color: TXT }}>{priceLabel}{isPaid && <span className="ml-2 align-middle text-sm text-white/40" style={{ fontFamily: SANS }}>one-time</span>}</div>
              </div>
              <ul className="flex flex-wrap justify-center gap-x-6 gap-y-2.5">
                {[course.total_lessons > 0 && `${course.total_lessons} ${labels.lessons || "lessons"}`, modules.length > 0 && `${modules.length} ${labels.modules || "modules"}`, "Lifetime access", "Learn on any device"].filter(Boolean).map((it, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm font-medium text-white/70"><Icon d="M4.5 12.75l6 6 9-13.5" className="h-4 w-4" stroke={2.2} style={{ color: A }} />{it}</li>
                ))}
              </ul>
              <button onClick={onEnroll} className="group flex items-center justify-center gap-2 rounded-full px-8 py-4 text-[0.78rem] font-bold uppercase tracking-[0.14em] text-black transition hover:-translate-y-0.5" style={{ background: A, boxShadow: `0 12px 30px ${rgba(A, 0.4)}` }}>{ctaLabel} <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>
            </div>
            {isPaid && landing.guarantee?.show && (
              <div className="border-t px-6 py-3.5 text-center text-[13px] sm:px-8" style={{ borderColor: LINE, background: rgba(A, 0.06) }}>
                <span className="font-bold text-white">{landing.guarantee.days || 30}-Day {labels.money_back_guarantee || "Money-Back Guarantee"}.</span> <span className="text-white/60">{labels.not_satisfied || "Not satisfied? Get a full refund."}</span>
              </div>
            )}
          </div>
        </Reveal>

        {/* SECTIONS */}
        <div className="space-y-24">
          {order.map((id) => <Fragment key={id}>{blocks[id]}</Fragment>)}
        </div>

        {/* FINAL CTA */}
        {landing.cta?.show !== false && (
          <Reveal>
            <section className="relative mt-20 overflow-hidden border px-6 py-20 text-center sm:px-12" style={{ background: CARD, borderColor: rgba(A, 0.25) }}>
              <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(60% 100% at 50% 0%, ${rgba(A, 0.22)}, transparent 60%)` }} />
              <div className="relative">
                <h2 className="text-[2.4rem] leading-tight text-white sm:text-[3.4rem]" style={{ fontFamily: SERIF, fontWeight: 600 }}>
                  {landing.cta?.heading ? landing.cta.heading : <>{t.before} <em style={{ fontStyle: "italic", color: A }}>{t.accent}</em> {t.after}</>}
                </h2>
                <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed text-white/60">{landing.cta?.subheading || sub || `Join ${course.name} and start learning today.`}</p>
                <button onClick={onEnroll} className="group mt-9 inline-flex items-center gap-2.5 rounded-full px-8 py-4 text-[0.8rem] font-bold uppercase tracking-[0.14em] text-black transition hover:-translate-y-0.5" style={{ background: A, boxShadow: `0 14px 36px ${rgba(A, 0.5)}` }}>{ctaLabel} <Icon d="M13 7l5 5m0 0l-5 5m5-5H6" className="h-4 w-4 transition-transform group-hover:translate-x-1" /></button>
              </div>
            </section>
          </Reveal>
        )}
      </div>

      {/* FOOTER */}
      <footer className="border-t" style={{ background: FOOT, borderColor: LINE }}>
        <div className="mx-auto flex max-w-6xl flex-col items-center justify-between gap-5 px-5 py-10 sm:flex-row sm:px-8">
          <div className="flex items-center gap-2.5">
            {Object.keys(SOCIAL_ICONS).filter((k) => landing.social?.[k]).map((k) => (
              <a key={k} href={landing.social[k]} target="_blank" rel="noopener noreferrer" aria-label={k} className="flex h-9 w-9 items-center justify-center text-white/70 transition hover:text-black" style={{ border: `1px solid ${LINE}` }} onMouseEnter={(e) => { e.currentTarget.style.background = A; e.currentTarget.style.borderColor = A; }} onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = LINE; }}>
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-current"><path d={SOCIAL_ICONS[k]} /></svg>
              </a>
            ))}
          </div>
          <PolicyLinks policies={policies} accent={A} dark />
        </div>
      </footer>

      <VideoModal video={landing.video} open={vid.open} onClose={vid.close} />
    </div>
  );
}

/* -------------------------------- helpers -------------------------------- */
function guaranteeDefaultHtml(days) {
  return `<p>We believe in the value of this course and are confident it will help you achieve your goals. That's why we offer a full ${days}-day risk-free money-back guarantee.</p><p>Go through the content, explore the lessons, and put what you learn into action. If at any point within the first ${days} days you feel the course isn't right for you or doesn't meet your expectations, simply reach out to us.</p><p>We'll process your refund promptly—no questions asked.</p><p>There's absolutely no risk in getting started today, only the opportunity to learn, grow, and succeed with complete peace of mind.</p>`;
}
function Grain() {
  const noise = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E";
  return <div className="pointer-events-none fixed inset-0 z-[1] opacity-[0.04] mix-blend-screen" style={{ backgroundImage: `url("${noise}")` }} />;
}

function Sec({ id, kicker, title, sub, pal, children }) {
  return (
    <section id={id} className="scroll-mt-24">
      <span className="inline-flex items-center gap-2 text-[0.7rem] font-bold uppercase tracking-[0.22em]" style={{ color: pal.A }}><span className="h-1.5 w-1.5 rotate-45" style={{ background: pal.B }} />{kicker}</span>
      <h2 className="mt-2 text-[2rem] leading-[1.05] text-white sm:text-[2.8rem]" style={{ fontFamily: SERIF, fontWeight: 600 }}>{title}</h2>
      {sub && <p className="mt-2 text-sm text-white/50">{sub}</p>}
      <span className="mt-6 block h-px w-full" style={{ background: `linear-gradient(to right, ${rgba(pal.B, 0.55)}, ${LINE} 42%)` }} />
      {children}
    </section>
  );
}

function Accordion({ modules, pal, labels }) {
  const [open, setOpen] = useState(0);
  return (
    <div className="mt-2">
      {modules.map((m, mi) => {
        const isOpen = open === mi;
        return (
          <div key={mi} className="border-b" style={{ borderColor: LINE }}>
            <button onClick={() => setOpen(isOpen ? -1 : mi)} className="flex w-full items-center gap-5 py-6 text-left">
              <span className="text-2xl italic tabular-nums" style={{ fontFamily: SERIF, fontWeight: 500, color: isOpen ? pal.A : "rgba(255,255,255,.3)" }}>{String(mi + 1).padStart(2, "0")}</span>
              <span className="flex-1 text-[1.15rem] text-white" style={{ fontFamily: SERIF, fontWeight: 500 }}>{m.name}</span>
              <span className="hidden text-xs uppercase tracking-wide sm:block" style={{ color: MUT }}>{m.lessons?.length || 0} {labels.lessons || "lessons"}</span>
              <Icon d={isOpen ? "M5 12h14" : "M12 5v14m7-7H5"} className="h-4 w-4 shrink-0" style={{ color: isOpen ? pal.A : "#fff" }} />
            </button>
            <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
              <div className="overflow-hidden">
                <ul className="pb-6 pl-12">
                  {(m.lessons || []).map((l, li) => (
                    <li key={li} className="flex items-center gap-3 border-t py-3" style={{ borderColor: LINE }}>
                      <LessonIcon lesson={l} color={pal.A} muted="rgba(255,255,255,.3)" className="h-4 w-4 shrink-0" />
                      <span className="flex-1 truncate text-sm" style={{ color: l.locked ? "rgba(255,255,255,.4)" : "rgba(255,255,255,.75)" }}>{l.title}</span>
                      {l.completed && <span className="text-xs font-semibold" style={{ color: pal.A }}>Done</span>}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Testimonials({ testimonials, pal, labels }) {
  const list = (testimonials?.list || []).filter((t) => t.quote || t.name);
  if (!testimonials?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec kicker={labels.testimonials || "Testimonials"} title={testimonials.heading || "What students are saying"} pal={pal}>
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {list.map((t, i) => (
            <figure key={i} className="flex flex-col p-7" style={{ background: CARD, border: `1px solid ${LINE}` }}>
              <span className="text-6xl leading-none" style={{ fontFamily: SERIF, color: pal.A }}>&ldquo;</span>
              <Stars value={t.rating} empty="rgba(255,255,255,.16)" color={pal.A} className="-mt-3 h-4 w-4" />
              <blockquote className="mt-4 flex-1 text-[1.1rem] leading-relaxed text-white/90" style={{ fontFamily: SERIF, fontWeight: 500 }}>{t.quote}</blockquote>
              <figcaption className="mt-6 flex items-center gap-3 border-t pt-5" style={{ borderColor: LINE }}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden text-sm font-bold text-black" style={{ background: pal.A }}>{t.avatar ? <img src={t.avatar} alt="" className="h-full w-full object-cover" /> : (t.name || "?").charAt(0).toUpperCase()}</span>
                <span className="min-w-0"><span className="block truncate text-sm font-semibold text-white">{t.name}</span>{t.role && <span className="block truncate text-xs" style={{ color: MUT }}>{t.role}</span>}</span>
              </figcaption>
            </figure>
          ))}
        </div>
      </Sec>
    </Reveal>
  );
}

function Guarantee({ guarantee, pal }) {
  if (!guarantee?.show) return null;
  const days = guarantee.days || 30;
  return (
    <Reveal>
      <section className="relative overflow-hidden border px-7 py-12 sm:px-10" style={{ background: CARD, borderColor: rgba(pal.A, 0.25) }}>
        <div className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(60% 120% at 88% 50%, ${rgba(pal.A, 0.25)}, transparent 60%)` }} />
        {guarantee.bg_image ? <img src={guarantee.bg_image} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20" /> : null}
        <div className="relative flex flex-col items-center gap-9 text-center sm:flex-row sm:text-left">
          <div className="order-2 flex-1 sm:order-1">
            <h3 className="text-[1.7rem] leading-tight text-white sm:text-[2.1rem]" style={{ fontFamily: SERIF, fontWeight: 600 }}>{guarantee.heading || `${days}-Day Risk-Free Money-Back Guarantee`}</h3>
            <div className="rich-content mt-3 text-[15px] leading-relaxed text-white/65" dangerouslySetInnerHTML={{ __html: guarantee.description || guaranteeDefaultHtml(days) }} />
          </div>
          <div className="order-1 shrink-0 sm:order-2">
            {guarantee.image
              ? <img src={guarantee.image} alt="" className="h-40 w-40 object-contain drop-shadow-2xl" />
              : <GuaranteeBadge variant={guarantee.badge || 1} days={days} accent={pal.A} className="h-40 w-40 drop-shadow-2xl sm:h-44 sm:w-44" />}
          </div>
        </div>
      </section>
    </Reveal>
  );
}

function Faq({ faq, pal, labels }) {
  const list = (faq?.list || []).filter((f) => f.q);
  const [open, setOpen] = useState(-1);
  if (!faq?.show || !list.length) return null;
  return (
    <Reveal>
      <Sec kicker={labels.faq || "FAQ"} title={faq.heading || "Frequently asked questions"} pal={pal}>
        <div className="mt-2">
          {list.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={i} className="border-b" style={{ borderColor: LINE }}>
                <button onClick={() => setOpen(isOpen ? -1 : i)} className="flex w-full items-center gap-5 py-5 text-left">
                  <span className="flex-1 text-[1.1rem] text-white" style={{ fontFamily: SERIF, fontWeight: 500 }}>{f.q}</span>
                  <Icon d={isOpen ? "M5 12h14" : "M12 5v14m7-7H5"} className="h-4 w-4 shrink-0" style={{ color: isOpen ? pal.A : "#fff" }} />
                </button>
                <div className="grid transition-all duration-300" style={{ gridTemplateRows: isOpen ? "1fr" : "0fr" }}>
                  <div className="overflow-hidden"><p className="pb-5 text-[15px] leading-relaxed" style={{ color: MUT }}>{f.a}</p></div>
                </div>
              </div>
            );
          })}
        </div>
      </Sec>
    </Reveal>
  );
}
